import { without } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import {
  useGetIngredientWholeList,
  usePutIngredientData,
  usePutSupplierData
  // ✅ Add missing import
} from "../../../api/ingredients";
import { InputText } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { DrawingContext } from "../../../components/drawing-tool/DrawingTool";
import MainBottomButtonView from "../../../components/mainBottomButtonView/MainBottomButtonView";
import { DISABLE_SIMULATION_INGREDIENT_DETAILS } from "../../../config/constants";
import { successMessage } from "../../../helpers/ErrorHandler";
import useWindowDimensions from "../../../hooks/windowsSize";
import IngredientEnvDetails from "../../ingredients/ingredientSimulation/IngredientEnvDetails";
import IngredientLcaDetails from "../../ingredients/ingredientSimulation/IngredientLcaDetails";
import IngredientPackagingDetails from "../../ingredients/ingredientSimulation/IngredientPackagingDetails";
import IngredientSupplierDetails from "../../ingredients/ingredientSimulation/IngredientSupplierDetails";
import IngredientTransportDetails from "../../ingredients/ingredientSimulation/IngredientTransportDetails";

const initialIngredient = {
  id: null,
  title: "",
  suppliers: [],
  productIds: [],
  ingredient_source: ""
};

const initialSupplier = {
  id: null,
  title: "",
  supplierData: {
    ingredientIds: [],
    productIds: []
  }
};
interface SupplierIngredientDetails {
  unitSize: number;
  unitType: string;
  eanBarcode: string;
  productId: string;
}

interface EnvData {
  ingredientType: string;
  dataType: string;
  country: number;
  farmingType: string;
  fieldType: string;
  produceType: string;
  produceCultivar: string;
  specifyIngredientValue: string;
}

interface IngredientLcaData {
  acidification: number;
  climateChange: number;
  ecotoxicityFreshWater: number;
  eutrophicationFreshWater: number;
  eutrophicationMarine: number;
  eutrophicationTerrestrial: number;
  humanToxicityCancer: number;
  humanToxicityNonCancer: number;
  ionisingRadiation: number;
  landUse: number;
  ozoneDepletion: number;
  particulateMatter: number;
  photochemicalOzoneFormation: number;
  resourceUseFossils: number;
  resourceUseMineralsAndMetals: number;
  waterUse: number;
}

interface originalSupplierIngredientData {
  envData: EnvData;
  lcaData: IngredientLcaData;
  supplierIngredientDetails: SupplierIngredientDetails;
}

export const IngredientBase = ({ modalData }: any) => {
  const { nodeItemId, chartEdges, chartNodes, ownerId, setNodeItem, setChartNodes, saveDrawing } =
    useContext(DrawingContext);

  // get chart node item from node id
  const { deleteElements, getNode } = useReactFlow();
  const [ingredient, setIngredient] = useState(initialIngredient);
  const [supplier, setSupplier] = useState(initialSupplier);
  const [visible, setVisible] = useState(false);
  const [simulationSupplierIngredientData, setSimulationSupplierIngredientData] =
    useState<SupplierIngredientDetails | null>(null);
  const [envDetails, setEnvDetails] = useState<EnvData | null>(null);
  const [isEnvDataAvailable, setIsEnvDataAvailable] = useState(true);

  const [ingredientId, setIngredientId] = useState<string>("");
  const [supplierId, setSupplierId] = useState<string>("");

  const lcaData: IngredientLcaData = useMemo(
    () => ({
      acidification: 2,
      climateChange: 0,
      ecotoxicityFreshWater: 0,
      eutrophicationFreshWater: 0,
      eutrophicationMarine: 0,
      eutrophicationTerrestrial: 0,
      humanToxicityCancer: 0,
      humanToxicityNonCancer: 0,
      ionisingRadiation: 0,
      landUse: 0,
      ozoneDepletion: 0,
      particulateMatter: 0,
      photochemicalOzoneFormation: 0,
      resourceUseFossils: 0,
      resourceUseMineralsAndMetals: 0,
      waterUse: 0
    }),
    []
  );

  const enablePackagingAndTransport = useMemo(() => {
    if (
      (envDetails?.ingredientType === "RAW" && envDetails?.dataType === "GENERIC") ||
      ingredient?.suppliers?.some(
        (supplier: {
          supplierIngredientDetails?: { data_type: string; ingredient_type: string };
        }) =>
          supplier?.supplierIngredientDetails?.data_type === "GENERIC" &&
          supplier?.supplierIngredientDetails?.ingredient_type === "RAW"
      )
    ) {
      return true;
    }
    return false;
  }, [envDetails, ingredient]);

  const originalSupplierIngredientData: originalSupplierIngredientData | null = useMemo(() => {
    const supplier = ingredient?.suppliers?.find(
      (supplier: any) => supplier?.supplierIngredientDetails
    ) as any;
    console.log("supplier", supplier);
    if (!supplier?.supplierIngredientDetails) {
      return null;
    }

    const supplierDetails = supplier.supplierIngredientDetails[0];
    return {
      envData: {
        ingredientType: supplierDetails.ingredient_type || "",
        dataType: supplierDetails.data_type || "",
        country: supplierDetails.countryOrigin || 0,
        farmingType: supplierDetails.farmingType || "",
        fieldType: supplierDetails.fieldType || "",
        produceType: supplierDetails.produceType || "",
        produceCultivar: supplierDetails.produceCultivar || "",
        specifyIngredientValue: supplierDetails.specifyIngredient || ""
      },
      lcaData: lcaData,
      supplierIngredientDetails: {
        unitSize: supplierDetails.unit_size || 0,
        unitType: supplierDetails.unit_type || "",
        eanBarcode: supplierDetails.ean_bar_code || "",
        productId: supplierDetails.product_id || ""
      }
    };
  }, [ingredient, lcaData]);

  const values = useMemo(() => {
    return {
      ingredientName: ingredient.title,
      supplierName: supplier.title
    };
  }, [nodeItemId, ingredient, supplier]);

  const {
    register,
    control,
    formState: { errors }
  } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const nodeItem = useMemo(() => getNode(nodeItemId || ""), [nodeItemId]);

  const {
    data: ingredientListByProduct,
    isLoading: isIngredientListByProductLoading,
    isSuccess
  } = useGetIngredientWholeList(false);

  const { isLoading: updateIngredientDataLoading, mutateAsync: mutatePutIngredient } =
    usePutIngredientData();

  const { mutateAsync: mutatePutSupplier } = usePutSupplierData();

  useEffect(() => {
    if (isSuccess) {
      const ingredient = ingredients?.find(
        (ing: any) => ing?.id === nodeItem?.data?.reference?.ingredientId
      );
      const supplier = ingredient?.suppliers?.find(
        (sup: any) => sup?._id === nodeItem?.data?.reference?.supplierId
      );
      setIngredient(ingredient || initialIngredient);
      setSupplier({
        id: supplier?._id || null,
        title: supplier?.supplier_name || "",
        supplierData: supplier
      });
    }
  }, [nodeItem, isSuccess]);

  const { height } = useWindowDimensions();

  const ingredients = useMemo(() => {
    return ingredientListByProduct?.map((pi: any) => {
      return {
        id: pi.ingredientData?._id,
        title: pi.ingredientData?.ingredient_name,
        suppliers: pi.ingredientData?.suppliers,
        productIds: pi.ingredientData?.productIds,
        ingredient_source: pi.ingredientData?.ingredient_source
      };
    });
  }, [ingredientListByProduct]);

  const updateIngredient = (status: any) => {
    let checkProductIngredientAvailable = ingredient?.productIds.some((e: any) => e === ownerId);

    if (checkProductIngredientAvailable && !status) return;
    const withoutProduct = without(ingredient.productIds, ownerId);
    let updateProduct = [...ingredient.productIds, ownerId];
    const updateIngredientData = {
      _id: ingredient.id,
      ingredient_name: ingredient.title,
      ownerIds: status ? withoutProduct : updateProduct,
      ingredient_source: ingredient.ingredient_source
    };
    mutatePutIngredient(updateIngredientData, {
      onSuccess(data: any) {
        successMessage("Update Ingredient");
      }
    });
  };

  const enableLCAData = useMemo(() => {
    if (!envDetails) {
      return false;
    }
    if (envDetails.dataType === "CALCULATED_LCA") {
      return true;
    }
  }, [envDetails]);

  useEffect(() => {
    if (isSuccess) {
      const ingredient = ingredients?.find(
        (ing: any) => ing?.id === nodeItem?.data?.reference?.ingredientId
      );
      const supplier = ingredient?.suppliers?.find(
        (sup: any) => sup?._id === nodeItem?.data?.reference?.supplierId
      );
      setIngredient(ingredient || initialIngredient);
      setSupplier({
        id: supplier?._id || null,
        title: supplier?.supplier_name || "",
        supplierData: supplier
      });

      setIngredientId(ingredient?.id || null);
      setSupplierId(supplier?._id || null);
    }
  }, [nodeItem, isSuccess, ingredients]);

  const updateSupplierData = (status: any) => {
    let checkProductIngredientAvailable = supplier?.supplierData?.ingredientIds?.some(
      (e: any) => e === ingredient.id
    );

    let checkProductAvailable = supplier?.supplierData?.productIds?.some((e: any) => e === ownerId);

    if (checkProductIngredientAvailable && checkProductAvailable && !status) return;

    const withoutProduct = without(ingredient.productIds, ownerId);

    let ownerIds = status ? withoutProduct : [...supplier.supplierData.productIds, ownerId];

    let supplierDataUpdate = {
      _id: supplier.id,
      supplier_name: supplier?.title,
      productIds: ownerIds
    };
    mutatePutSupplier(supplierDataUpdate, {
      onSuccess(data: any) {
        successMessage("Supplier update successfully");
      }
    });
  };

  const deleteIngredient = () => {
    setVisible(true);
  };

  const deleteIngredientNode = () => {
    updateIngredient(true);
    updateSupplierData(true);
    deleteElements({ nodes: [{ id: nodeItem?.id || "" }] });
    setNodeItem(null);
  };

  // submit callback function
  const onSubmit = useCallback(() => {
    updateIngredient(false);
    updateSupplierData(false);
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        label: ingredient.title,
        // reference is mongoose mix type so you can set any type of here please set necessary reference only
        reference: {
          ingredientId: ingredient.id,
          supplierId: supplier.id
        },
        description: `${ingredient.title} ${supplier.title}`
      }
    };
    const updatedChartNodes = chartNodes.map((n: any) => {
      if (n.id === nodeItem?.id) {
        return updatedNode;
      }
      return n;
    });

    setChartNodes(updatedChartNodes);
    setNodeItem(updatedNode);
    saveDrawing(chartEdges, updatedChartNodes);
  }, [nodeItem, chartEdges, chartNodes, ingredient, supplier]);

  // Loading state check
  if (isIngredientListByProductLoading || updateIngredientDataLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const disableEdit = ingredient.id && nodeItem?.data.reference && supplier.id ? true : false;

  return (
    <>
      <div>
        <form
          onSubmit={e => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <CommonSplitContainer
            currentHeight={0.87}
            topChild={
              <div>
                <div>
                  <Grid columns={2}>
                    <Grid.Row columns={2}>
                      <Grid.Column computer={8} tablet={8} mobile={16}>
                        <InputText
                          register={register}
                          placeholder="Ingredient Name"
                          name="ingredientName"
                          labelName={"Ingredient Name*"}
                          customGridMain="customIngredientNameView"
                          disabled={true}
                        />
                      </Grid.Column>
                      <Grid.Column computer={8} tablet={8} mobile={16}>
                        <InputText
                          register={register}
                          placeholder="Supplier Name"
                          name="supplierName"
                          labelName={"Supplier Name*"}
                          disabled={true}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </div>
                {DISABLE_SIMULATION_INGREDIENT_DETAILS && (
                  <div>
                    <IngredientSupplierDetails
                      simulationSupplierIngredientData={simulationSupplierIngredientData || null}
                      originalSupplierIngredientData={
                        originalSupplierIngredientData?.supplierIngredientDetails || null
                      }
                    />
                    <IngredientEnvDetails
                      originalEnvData={originalSupplierIngredientData?.envData || null}
                      simulatedEnvData={envDetails || null}
                      setSimulatedEnvData={setEnvDetails}
                      register={register}
                      control={control}
                      errors={errors}
                      isEnvDataAvailable={isEnvDataAvailable}
                      setIsEnvDataAvailable={setIsEnvDataAvailable}
                    />
                    {enablePackagingAndTransport ? (
                      <>
                        <IngredientPackagingDetails />
                      </>
                    ) : null}

                    {enablePackagingAndTransport ? <IngredientTransportDetails /> : null}

                    {enableLCAData ? (
                      <>
                        <IngredientLcaDetails lcaData={lcaData} />
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            }
            bottomChild={
              <MainBottomButtonView
                saveButtonStatus={true}
                deleteStatus={disableEdit}
                saveTitle={"Update"}
                saveButton={onSubmit}
                deleteButton={deleteIngredient}
                type="submit"
              />
            }
          />
        </form>
      </div>
      <ConfirmModal
        viewModal={visible}
        closeModal={() => setVisible(false)}
        cancel={() => {
          setVisible(false);
        }}
        approve={() => {
          deleteIngredientNode();
        }}
        title="Delete Ingredient"
        subTitle="Are you sure you want to remove the ingredient data?"
      />
    </>
  );
};
